// Function to get environment-specific title suffix
const getEnvironmentTitle = (): string => {
  const hostname = window.location.hostname;

  if (hostname === 'localhost') {
    return 'Local Zuul 4.0';
  } else if (hostname === 'zuul-dev.whatifops.com') {
    return 'Dev Zuul 4.0';
  } else if (hostname === 'zuul-stage.whatifops.com') {
    return 'Stage Zuul 4.0';
  } else if (hostname === 'zuul-prod-test.whatifops.com') {
    return 'Prod-Test Zuul 4.0';
  } else if (hostname === 'zuul.whatifops.com') {
    return 'Zuul 4.0';
  } else {
    return 'Zuul 4.0';
  }
};

// Page title mapping for different routes
export const PAGE_TITLES : { [key: string]: string} = {
  '/dashboard': 'Dashboard',
  '/advertisers': 'Advertisers',
  '/permissions': 'Permissions',
  '/marketingpartners': 'Marketing Partners',
  '/domains': 'Domains',
  '/affiliates': 'Affiliates',
  '/publisher-pre-pings': 'Publisher Pre Pings',
  '/publisher-pre-pings/create': 'Create Publisher Pre Ping',
  '/advertiser-pre-pings': 'Advertiser Pre Pings',
  '/advertiser-pre-pings/create': 'Create Advertiser Pre Ping',
  '/revshare-percentage': 'Rev Share Percentage',
  '/campaigns/landingpages': 'Landing Pages',
  '/campaigns/landingpages/landingpage-detail': 'Landing Page Details',
  '/campaigns/landingpagegroups': 'Landing Page Groups',
  '/campaigns/landingpagegroups/create': 'Create Landing Page Group',
  '/campaigns/offerblocks': 'Offer Blocks',
  '/campaigns/offertable': 'Offers',
  '/campaigns/offertable/offer-detail': 'Offer Details',
  '/campaigns/offerwallads': 'Offer Wall Ads',
  '/campaigns/offerwallads/create': 'Create Offer Wall Ad',
  '/campaigns': 'Campaigns',
  '/campaigns/dynamic-offer-sections': 'Dynamic Offer Sections',
  '/base/navbars': 'Navigation Bars',
  '/campaigns/offeractions': 'Offer Actions',
  '/campaigns/offeractions/offer-action-detail': 'Offer Action Details',
  '/users': 'Users',
  '/campaigns/sequences': 'Sequences',
  '/campaigns/tags': 'Tags',
  '/content/layouts': 'Layouts',
  '/content/layouts/create': 'Create Layout',
  '/content/spa/layouts/create': 'Create SPA Layout',
  '/content/containers': 'Containers',
  '/content/containers/create': 'Create Container',
  '/media-library': 'Media Library',
  '/features': 'Feature Toggles',
  '/content/handlers': 'Handlers',
  '/content/template-editor': 'Template Editor',
  '/tools/test-link-builder': 'Test Link Builder',
  '/content/userAttributes': 'User Attributes',
  '/custom-properties/properties': 'Properties',
  '/custom-properties/property-sets': 'Property Sets',
  '/custom-properties/property-sets/create': 'Create Property Set',
  '/custom-properties/entity-groups': 'Entity Groups',
  '/custom-properties/global-properties': 'Global Properties',
  '/campaigns/sites': 'Sites',
  '/field-mappings': 'Field Mappings'
};

// Routes that should include ID in the title when viewing details
const DETAIL_ROUTES_WITH_ID = [
  { pattern: /^\/campaigns\/offertable\/offer\/(\d+)/, title: 'Offer {id}: {name}' },
  { pattern: /^\/campaigns\/landingpages\/(\d+)/, title: 'LP {id}: {name}' },
  { pattern: /^\/campaigns\/landingpagegroups\/(\d+)/, title: 'LPG {id}: {name}' },
  { pattern: /^\/campaigns\/offerwallads\/(\d+)/, title: 'OW Ad {id}: {name}' },
  { pattern: /^\/campaigns\/offeractions\/(\d+)/, title: 'Action {id}: {name}' },
  { pattern: /^\/advertiser-pre-pings\/(\d+)/, title: 'APP {id}: {name}' },
  { pattern: /^\/publisher-pre-pings\/(\d+)/, title: 'PPP {id}: {name}' },
  { pattern: /^\/userdetails\/(\d+)/, title: 'User {id}: {name}' },
  { pattern: /^\/content\/layouts\/(\d+)/, title: 'Layout {id}: {name}' },
  { pattern: /^\/content\/spa\/layouts\/(\d+)/, title: 'SPA Layout {id}: {name}' },
  { pattern: /^\/content\/containers\/(\d+)/, title: 'Container {id}: {name}' },
  { pattern: /^\/custom-properties\/property-sets\/(\d+)/, title: 'Set {id}: {name}' },
  { pattern: /^\/custom-properties\/entity-groups\/(\d+)/, title: 'EG {id}: {name}' },
  { pattern: /^\/campaigns\/sites\/(\d+)/, title: 'Site {id}: {name}' },
  { pattern: /^\/campaigns\/sequences\/(\d+)/, title: 'Seq {id}: {name}' },
  { pattern: /^\/campaigns\/offerblocks\/(\d+)/, title: 'OB {id}: {name}' },
];

// Function to get page title based on current pathname
export const getPageTitle = (pathname : string, entityName: string)  => {
  const environmentTitle = getEnvironmentTitle();

  // Check for the exact match first
  if (PAGE_TITLES[pathname]) {
    return `${PAGE_TITLES[pathname]} - ${environmentTitle}`;
  }

  // Check for detail routes with ID
  for (const route of DETAIL_ROUTES_WITH_ID) {
    const match = pathname.match(route.pattern);
    if (match) {
      const id = match[1];
      const title = route.title.replace('{id}', id || '').replace('{name}', entityName || 'Details');
      return `${title} - ${environmentTitle}`;
    }
  }

  // Check for dynamic routes with parameters
  for (const route in PAGE_TITLES) {
    if (route.includes(':')) {
      const routePattern = route.replace(/:[^/]+/g, '[^/]+');
      const regex = new RegExp(`^${routePattern}$`);
      if (regex.test(pathname)) {
        return `${PAGE_TITLES[route]} Details - ${environmentTitle}`;
      }
    }
  }

  // Check for nested routes by finding the closest parent route
  const pathSegments = pathname.split('/').filter(Boolean);
  for (let i = pathSegments.length; i > 0; i--) {
    const parentPath = '/' + pathSegments.slice(0, i).join('/');
    if (PAGE_TITLES[parentPath]) {
      return `${PAGE_TITLES[parentPath]} - ${environmentTitle}`;
    }
  }

  // Default title with environment
  return `${environmentTitle} - CMS`;
};
